/**
 * Composable para centralizar el funnel de PostHog: nombres de eventos
 * y propiedades comunes (hotel_id, room_id, pack, amount, currency, etc.).
 * Uso: const { trackSearchCompleted, trackHotelCardClicked, ... } = usePosthogFunnel()
 */

export const FunnelEvents = {
  SEARCH_COMPLETED: 'search_completed',
  HOTEL_CARD_CLICKED: 'hotel_card_clicked',
  HOTEL_VIEWED: 'hotel_viewed',
  ROOM_PACK_SELECTED: 'room_pack_selected',
  HOTEL_RESERVATION_CLICKED: 'hotel_reservation_clicked',
  CHECKOUT_STARTED: 'checkout_started',
  CHECKOUT_PAYMENT_SHOWN: 'checkout_payment_shown',
  CHECKOUT_COMPLETED: 'checkout_completed',
  COOKIE_CONSENT_ACCEPTED: 'cookie_consent_accepted',
  ALTERNATIVE_NEARBY_DATES_HOTEL_CLICKED: 'alternative_nearby_dates_hotel_clicked',
  ALTERNATIVE_NEARBY_HOTEL_CLICKED: 'alternative_nearby_hotel_clicked',
  ALTERNATIVE_MODULE_VIEWED: 'alternative_module_viewed'
} as const

export type FunnelEventName = (typeof FunnelEvents)[keyof typeof FunnelEvents]

export interface SearchCompletedProperties {
  destination_id?: number | null
  destination_name?: string
  date: string
  currency: string
  locale?: string
  results_count?: number
  has_destination: boolean
}

export interface HotelCardClickedProperties {
  hotel_id: number
  hotel_name?: string
  destination_id?: number | null
  destination_name?: string
  date?: string
  currency?: string
  min_price?: string | number
  is_mobile?: boolean
}

export interface HotelViewedProperties {
  hotel_id: number
  hotel_name?: string
  destination_id?: number | null
  destination_name?: string
  date?: string
  currency?: string
  rooms_count?: number
}

export interface RoomPackSelectedProperties {
  hotel_id: number
  hotel_name?: string
  room_id: number
  room_type?: string
  pack_hours: number
  checkin_hour?: string
  price?: string
  currency?: string
  date?: string
  is_mobile?: boolean
}

export interface HotelReservationClickedProperties {
  hotel_id: number
  hotel_name?: string
  room_id: number
  room_type?: string
  pack_hours: number
  checkin_hour?: string
  num_rooms?: number
  price?: string | number
  currency?: string
  date?: string
  extras_selected?: string
  is_mobile?: boolean
  button_location?: string
}

export interface CheckoutStartedProperties {
  hotel_id: number
  hotel_name?: string
  room_id: number
  pack_hours: number
  date?: string
  total_amount: number
  currency?: string
  num_rooms?: number
}

export interface CheckoutPaymentShownProperties {
  hotel_id: number
  hotel_name?: string
  room_id: number
  total_amount: number
  currency?: string
}

export interface CheckoutCompletedProperties {
  hotel_id?: number
  hotel_name?: string
  room_id?: number
  total_amount: number
  currency?: string
  date?: string
  pack_hours?: number
  reference?: string
}

export interface CookieConsentAcceptedProperties {
  button_id: string
  location: string
}

export interface AlternativeNearbyDatesHotelClickedProperties {
  hotel_id: number
  hotel_name?: string
  search_date: string
  available_date: string
  destination_id?: number | null
  currency?: string
  is_mobile?: boolean
}

export interface AlternativeNearbyHotelClickedProperties {
  clicked_hotel_id: number
  clicked_hotel_name?: string
  source_hotel_id: number
  source_hotel_name?: string
  date?: string
  currency?: string
  is_mobile?: boolean
}

export type AlternativeModuleType = 'nearby_dates' | 'alternatives' | 'nearby_hotels'

export interface AlternativeModuleViewedProperties {
  module_type: AlternativeModuleType
  items_count: number
  destination_id?: number | null
  destination_name?: string
  search_date?: string
  currency?: string
  locale?: string
  is_mobile?: boolean
}

export function usePosthogFunnel() {
  const posthog = usePostHog()

  function track(eventName: FunnelEventName | string, properties?: object) {
    if (import.meta.client && posthog?.capture) {
      posthog.capture(eventName, (properties ?? {}) as Record<string, unknown>)
    }
  }

  function trackSearchCompleted(properties: SearchCompletedProperties) {
    track(FunnelEvents.SEARCH_COMPLETED, properties)
  }

  function trackHotelCardClicked(properties: HotelCardClickedProperties) {
    track(FunnelEvents.HOTEL_CARD_CLICKED, properties)
  }

  function trackHotelViewed(properties: HotelViewedProperties) {
    track(FunnelEvents.HOTEL_VIEWED, properties)
  }

  function trackRoomPackSelected(properties: RoomPackSelectedProperties) {
    track(FunnelEvents.ROOM_PACK_SELECTED, properties)
  }

  function trackHotelReservationClicked(properties: HotelReservationClickedProperties) {
    track(FunnelEvents.HOTEL_RESERVATION_CLICKED, properties)
  }

  function trackCheckoutStarted(properties: CheckoutStartedProperties) {
    track(FunnelEvents.CHECKOUT_STARTED, properties)
  }

  function trackCheckoutPaymentShown(properties: CheckoutPaymentShownProperties) {
    track(FunnelEvents.CHECKOUT_PAYMENT_SHOWN, properties)
  }

  function trackCheckoutCompleted(properties: CheckoutCompletedProperties) {
    track(FunnelEvents.CHECKOUT_COMPLETED, properties)
  }

  function trackCookieConsentAccepted(properties: CookieConsentAcceptedProperties) {
    track(FunnelEvents.COOKIE_CONSENT_ACCEPTED, properties)
  }

  function trackAlternativeNearbyDatesHotelClicked(properties: AlternativeNearbyDatesHotelClickedProperties) {
    track(FunnelEvents.ALTERNATIVE_NEARBY_DATES_HOTEL_CLICKED, properties)
  }

  function trackAlternativeNearbyHotelClicked(properties: AlternativeNearbyHotelClickedProperties) {
    track(FunnelEvents.ALTERNATIVE_NEARBY_HOTEL_CLICKED, properties)
  }

  function trackAlternativeModuleViewed(properties: AlternativeModuleViewedProperties) {
    track(FunnelEvents.ALTERNATIVE_MODULE_VIEWED, properties)
  }

  return {
    track,
    trackSearchCompleted,
    trackHotelCardClicked,
    trackHotelViewed,
    trackRoomPackSelected,
    trackHotelReservationClicked,
    trackCheckoutStarted,
    trackCheckoutPaymentShown,
    trackCheckoutCompleted,
    trackCookieConsentAccepted,
    trackAlternativeNearbyDatesHotelClicked,
    trackAlternativeNearbyHotelClicked,
    trackAlternativeModuleViewed,
    FunnelEvents
  }
}
